import { InAppState } from "../base/InAppState";
import '../../components/template/test-center';
import template from './PackDetailTemplate.html';
import { Grid, html } from 'gridjs';
import Swal from 'sweetalert2/dist/sweetalert2.js';

export class PackDetailState extends InAppState
{
    static ROUTE = '/pack/view/:packId';
    static ID = 'pack-detail';

    constructor( app, rp )
    {
        super( app, rp );
        this.packId = rp.getParam( 'packId' );
        this.generatedDocumentsTable = null;
    }


    async enter()
    {
        this.pack = null;
        this.templates = [];

        const { result } = await this.app.api.get( `/pack/${this.packId}` );
        if ( result.status === 200 && result.data )
        {
            this.pack = result.data;
        }

        // Load all templates belonging to this pack
        if ( this.pack && this.pack.templateIds && this.pack.templateIds.length > 0 )
        {
            const templatePromises = this.pack.templateIds.map( ( templateId ) =>
                this.app.api.get( `/template/${templateId}` )
            );

            const templateResponses = await Promise.all( templatePromises );
            templateResponses.forEach( ( response ) =>
            {
                if ( response.result.status === 200 && response.result.data )
                {
                    this.templates.push( response.result.data );
                }
            });
        }

        this.render();

        if ( this.pack )
        {
            this.addEventListener(
                this.app.view.pageContent.querySelector( '[data-type="pack-delete-button"]' ),
                'click',
                async ( e ) => {
                    this.deletePack();
                }
            );

            this._bindToggleStatusButton();

            this.app.view.pageContent.querySelectorAll( '[data-type="pack-tab"]' ).forEach( ( tab ) => {
                this.addEventListener( tab, 'click', () => {
                    this.switchTab( tab.getAttribute( 'data-tab-target' ) );
                });
            });

            // Assign template data to each test-center component
            const testCenters = this.app.view.pageContent.querySelectorAll( 'test-center' );
            testCenters.forEach( ( testCenter ) => {
                const index = parseInt( testCenter.getAttribute( 'data-template-index' ), 10 );
                if ( this.templates[ index ] )
                {
                    testCenter.template = this.templates[ index ];
                }
            });

            // Bind placeholders toggles for each template
            const placeholdersToggles = this.app.view.pageContent.querySelectorAll( '[data-type="placeholders-toggle"]' );
            placeholdersToggles.forEach( ( toggle ) => {
                this.addEventListener( toggle, 'click', () => {
                    const tmplIndex = toggle.getAttribute( 'data-template-index' );
                    this._togglePlaceholders( tmplIndex );
                });
            });

            this._bindPlaceholderDescriptionEditing();
            this._bindEditButton();
            this._bindPackTestButtons();
        }
    }

    _bindEditButton()
    {
        const editBtn = this.app.view.pageContent.querySelector( '[data-type="pack-edit-button"]' );
        if ( editBtn )
        {
            this.addEventListener( editBtn, 'click', () => {
                this.app.router.redirect( `/pack/edit/${this.packId}` );
            });
        }
    }

    _bindPackTestButtons()
    {
        const runBtn = this.app.view.pageContent.querySelector( '[data-type="pack-run-test"]' );
        if ( runBtn )
        {
            this.addEventListener( runBtn, 'click', () => {
                this._onRunPackTest();
            });
        }

        const codeBtn = this.app.view.pageContent.querySelector( '[data-type="pack-show-code"]' );
        if ( codeBtn )
        {
            this.addEventListener( codeBtn, 'click', () => {
                this._onShowPackExampleCode();
            });
        }
    }

    _collectAllTemplateData()
    {
        const templateData = [];
        const testCenters = this.app.view.pageContent.querySelectorAll( 'test-center' );

        testCenters.forEach( ( testCenter ) => {
            const index = parseInt( testCenter.getAttribute( 'data-template-index' ), 10 );
            const tmpl = this.templates[ index ];
            if ( !tmpl ) return;

            const formData = testCenter._collectFormData();
            templateData.push({
                templateId: tmpl.id,
                variables: formData.variables
            });
        });

        return templateData;
    }

    async _onRunPackTest()
    {
        if ( !this.pack || this.templates.length === 0 ) return;

        const templateData = this._collectAllTemplateData();

        const pageContent = this.app.view.pageContent;
        this.app.view.blockUI( pageContent, 'Generating pack document...' );

        try
        {
            const metadataResponse = await fetch( `${this.app.api.getUrl()}/generation/pack`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                    'x-version': this.app.api._api.headers.get( 'x-version' )
                },
                body: JSON.stringify({
                    data: {
                        packId: this.pack.id,
                        templateData: templateData,
                        metadataOnly: true
                    }
                })
            });

            this.app.view.unblockUI( pageContent );

            if ( !metadataResponse.ok )
            {
                throw new Error( `Pack generation failed: ${metadataResponse.statusText}` );
            }

            const metadata = await metadataResponse.json();

            const result = await Swal.fire({
                title: 'Pack Document Ready',
                html: this._buildMetadataHtml( metadata ),
                showCancelButton: true,
                confirmButtonText: '<i class="ti ti-download me-1"></i> Download',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#6c757d'
            });

            if ( result.isConfirmed )
            {
                await this._downloadPackDocument( metadata.filename );
            }
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.unblockUI( pageContent );
            this.app.view.toast( 'Failed to generate pack document', 'error' );
        }
    }

    _buildMetadataHtml( metadata )
    {
        const items = [];

        if ( metadata.filename )
        {
            items.push( `<div class="mb-2"><strong>Filename:</strong> ${metadata.filename}</div>` );
        }

        if ( metadata.filesize )
        {
            const kb = metadata.filesize / 1024;
            const sizeStr = kb < 1024 ? `${kb.toFixed( 1 )} KB` : `${( kb / 1024 ).toFixed( 1 )} MB`;
            items.push( `<div class="mb-2"><strong>Filesize:</strong> ${sizeStr}</div>` );
        }

        if ( metadata.createdAt )
        {
            items.push( `<div class="mb-2"><strong>Generated:</strong> ${new Date( metadata.createdAt ).toLocaleString()}</div>` );
        }

        if ( items.length === 0 )
        {
            items.push( '<div class="text-muted">Document is ready for download.</div>' );
        }

        return `<div class="text-start">${items.join( '' )}</div>`;
    }

    async _downloadPackDocument( filename )
    {
        const pageContent = this.app.view.pageContent;
        this.app.view.blockUI( pageContent, 'Downloading document...' );

        try
        {
            const response = await fetch( `${this.app.api.getUrl()}/generation/pack/${this.packId}/${filename}`, {
                method: 'GET',
                headers: {
                    'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                    'x-version': this.app.api._api.headers.get( 'x-version' )
                }
            });

            if ( !response.ok )
            {
                throw new Error( `Download failed: ${response.statusText}` );
            }

            const blob = await response.blob();
            const url = URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = filename;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
            URL.revokeObjectURL( url );

            this.app.view.unblockUI( pageContent );
            this.app.view.toast( 'Document downloaded successfully', 'success' );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.unblockUI( pageContent );
            this.app.view.toast( 'Failed to download document', 'error' );
        }
    }

    _onShowPackExampleCode()
    {
        if ( !this.pack || this.templates.length === 0 ) return;

        const templateData = this._collectAllTemplateData();
        const apiUrl = this.app.api.getUrl();
        const endpoint = `${apiUrl}/generation/pack`;

        const dataPayload = JSON.stringify({
            packId: this.pack.id,
            templateData: templateData
        }, null, 2 );

        const tabs = [
            { id: 'curl',   label: 'cURL',    code: this._genCurl( endpoint, dataPayload ) },
            { id: 'php',    label: 'PHP',      code: this._genPhp( endpoint, dataPayload ) },
            { id: 'nodejs', label: 'Node.js',  code: this._genNode( endpoint, dataPayload ) },
            { id: 'python', label: 'Python',   code: this._genPython( endpoint, dataPayload ) },
            { id: 'dotnet', label: '.NET',      code: this._genDotnet( endpoint, dataPayload ) }
        ];

        const tabHeaders = tabs.map( ( tab, i ) =>
            `<li class="nav-item">
                <a href="javascript:void(0)" class="nav-link gap-6 note-link d-flex align-items-center justify-content-center px-3 px-md-3${i === 0 ? ' active' : ''}" data-tab="${tab.id}">${tab.label}</a>
            </li>`
        ).join( '' );

        const tabContents = tabs.map( ( tab, i ) =>
            `<div class="tab-pane${i === 0 ? ' active' : ''}" data-tab-content="${tab.id}">
                <pre class="mb-0" style="max-height: 400px; overflow: auto; text-align: left; background: #1e1e1e; color: #d4d4d4; padding: 1rem; border-radius: 0.375rem; font-size: 0.8rem; white-space: pre-wrap; word-break: break-all;"><code>${this._escapeHtml( tab.code )}</code></pre>
            </div>`
        ).join( '' );

        const htmlContent = `
            <ul class="nav nav-pills p-3 mb-3 rounded align-items-center card flex-row" id="codeExampleTabs">${tabHeaders}</ul>
            <div class="tab-content" id="codeExampleContent">${tabContents}</div>
        `;

        Swal.fire({
            title: 'API Example Code',
            html: htmlContent,
            width: '700px',
            showConfirmButton: false,
            showCloseButton: true,
            showCancelButton: true,
            cancelButtonText: 'Close',
            cancelButtonColor: '#6c757d',
            didOpen: ( popup ) => {
                popup.querySelectorAll( '.tab-pane:not(.active)' ).forEach( p => p.style.display = 'none' );
                const tabButtons = popup.querySelectorAll( '[data-tab]' );
                tabButtons.forEach( btn => {
                    btn.addEventListener( 'click', () => {
                        popup.querySelectorAll( '.nav-link' ).forEach( b => b.classList.remove( 'active' ) );
                        popup.querySelectorAll( '.tab-pane' ).forEach( p => { p.classList.remove( 'active' ); p.style.display = 'none'; } );
                        btn.classList.add( 'active' );
                        const pane = popup.querySelector( `[data-tab-content="${btn.dataset.tab}"]` );
                        pane.classList.add( 'active' );
                        pane.style.display = 'block';
                    });
                });
            }
        });
    }

    _escapeHtml( str )
    {
        return str.replace( /&/g, '&amp;' ).replace( /</g, '&lt;' ).replace( />/g, '&gt;' ).replace( /"/g, '&quot;' );
    }

    _genCurl( endpoint, dataPayload )
    {
        return `curl -X POST "${endpoint}" \\
  -H "x-apitoken: YOUR_API_TOKEN" \\
  -F 'data=${dataPayload}'`;
    }

    _genPhp( endpoint, dataPayload )
    {
        return `<?php

$apiToken = 'YOUR_API_TOKEN';
$endpoint = '${endpoint}';

$data = '${dataPayload.replace( /'/g, "\\'" )}';

$ch = curl_init($endpoint);
curl_setopt_array($ch, [
    CURLOPT_POST           => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER     => [
        'x-apitoken: ' . $apiToken,
    ],
    CURLOPT_POSTFIELDS     => ['data' => $data],
]);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($httpCode === 201) {
    file_put_contents('output.pdf', $response);
    echo "Document saved to output.pdf\\n";
} else {
    echo "Error: " . $response . "\\n";
}`;
    }

    _genNode( endpoint, dataPayload )
    {
        return `const FormData = require('form-data');
const fetch = require('node-fetch');
const fs = require('fs');

const API_TOKEN = 'YOUR_API_TOKEN';
const endpoint = '${endpoint}';

const data = JSON.stringify(${dataPayload});

const form = new FormData();
form.append('data', data);

const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
        'x-apitoken': API_TOKEN,
    },
    body: form,
});

if (response.status === 201) {
    const buffer = await response.buffer();
    fs.writeFileSync('output.pdf', buffer);
    console.log('Document saved to output.pdf');
} else {
    const error = await response.text();
    console.error('Error:', error);
}`;
    }

    _genPython( endpoint, dataPayload )
    {
        return `import requests
import json

API_TOKEN = 'YOUR_API_TOKEN'
endpoint = '${endpoint}'

data = json.dumps(${dataPayload})

response = requests.post(
    endpoint,
    headers={'x-apitoken': API_TOKEN},
    files={'data': (None, data)},
)

if response.status_code == 201:
    with open('output.pdf', 'wb') as f:
        f.write(response.content)
    print('Document saved to output.pdf')
else:
    print(f'Error: {response.text}')`;
    }

    _genDotnet( endpoint, dataPayload )
    {
        return `using System.Net.Http;
using System.Text.Json;

var apiToken = "YOUR_API_TOKEN";
var endpoint = "${endpoint}";

var payload = @"${dataPayload.replace( /"/g, '""' )}";

using var client = new HttpClient();
client.DefaultRequestHeaders.Add("x-apitoken", apiToken);

using var form = new MultipartFormDataContent();
form.Add(new StringContent(payload), "data");

var response = await client.PostAsync(endpoint, form);

if (response.StatusCode == System.Net.HttpStatusCode.Created) {
    var bytes = await response.Content.ReadAsByteArrayAsync();
    await File.WriteAllBytesAsync("output.pdf", bytes);
    Console.WriteLine("Document saved to output.pdf");
} else {
    var error = await response.Content.ReadAsStringAsync();
    Console.WriteLine($"Error: {error}");
}`;
    }

    _bindToggleStatusButton()
    {
        const toggleBtn = this.app.view.pageContent.querySelector( '[data-type="pack-toggle-status"]' );
        if ( toggleBtn )
        {
            this.addEventListener( toggleBtn, 'click', async () => {
                this._toggleStatus();
            });
        }
    }

    _rebindEventListeners()
    {
        this.addEventListener(
            this.app.view.pageContent.querySelector( '[data-type="pack-delete-button"]' ),
            'click',
            async () => {
                this.deletePack();
            }
        );

        this._bindToggleStatusButton();

        this.app.view.pageContent.querySelectorAll( '[data-type="pack-tab"]' ).forEach( ( tab ) => {
            this.addEventListener( tab, 'click', () => {
                this.switchTab( tab.getAttribute( 'data-tab-target' ) );
            });
        });

        const testCenters = this.app.view.pageContent.querySelectorAll( 'test-center' );
        testCenters.forEach( ( testCenter ) => {
            const index = parseInt( testCenter.getAttribute( 'data-template-index' ), 10 );
            if ( this.templates[ index ] )
            {
                testCenter.template = this.templates[ index ];
            }
        });

        const placeholdersToggles = this.app.view.pageContent.querySelectorAll( '[data-type="placeholders-toggle"]' );
        placeholdersToggles.forEach( ( toggle ) => {
            this.addEventListener( toggle, 'click', () => {
                const tmplIndex = toggle.getAttribute( 'data-template-index' );
                this._togglePlaceholders( tmplIndex );
            });
        });

        this._bindPlaceholderDescriptionEditing();
        this._bindEditButton();
        this._bindPackTestButtons();
    }

    _bindPlaceholderDescriptionEditing()
    {
        const descriptionCells = this.app.view.pageContent.querySelectorAll( '[data-type="placeholder-description"]' );

        descriptionCells.forEach( ( cell ) => {
            this.addEventListener( cell, 'click', ( e ) => {
                if ( cell.querySelector( '.description-input' ) )
                {
                    return;
                }
                this._startEditingDescription( cell );
            });
        });
    }

    _startEditingDescription( cell )
    {
        const key = cell.getAttribute( 'data-placeholder-key' );
        const templateId = cell.getAttribute( 'data-template-id' );
        const textSpan = cell.querySelector( '.description-text' );
        const placeholderSpan = cell.querySelector( '.description-placeholder' );
        const editIcon = cell.querySelector( '.description-edit-icon' );
        const currentValue = textSpan.textContent || '';

        textSpan.style.display = 'none';
        placeholderSpan.style.display = 'none';
        editIcon.style.display = 'none';

        const input = document.createElement( 'input' );
        input.type = 'text';
        input.className = 'description-input';
        input.value = currentValue;
        input.placeholder = 'Enter description...';
        cell.appendChild( input );
        input.focus();
        input.select();

        const saveDescription = async () => {
            const newValue = input.value.trim();

            if ( newValue === currentValue )
            {
                this._finishEditingDescription( cell, currentValue );
                return;
            }

            cell.classList.add( 'saving' );

            try
            {
                const response = await fetch( `${this.app.api.getUrl()}/template/${templateId}/placeholder`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                        'x-version': this.app.api._api.headers.get( 'x-version' )
                    },
                    body: JSON.stringify({ key, description: newValue })
                });

                cell.classList.remove( 'saving' );

                if ( response.ok )
                {
                    const updatedTemplate = await response.json();

                    // Update the template in our local array
                    const tmplIndex = this.templates.findIndex( ( t ) => t.id === templateId );
                    if ( tmplIndex !== -1 )
                    {
                        this.templates[ tmplIndex ] = updatedTemplate;

                        // Update the corresponding test-center component
                        const testCenter = this.app.view.pageContent.querySelector( `test-center[data-template-index="${tmplIndex}"]` );
                        if ( testCenter )
                        {
                            testCenter.template = updatedTemplate;
                        }
                    }

                    this._finishEditingDescription( cell, newValue );
                    this.app.view.toast( 'Description updated', 'success' );
                }
                else
                {
                    const errorData = await response.json().catch( () => null );
                    this.app.view.toast( errorData?.message || 'Failed to update description', 'error' );
                    this._finishEditingDescription( cell, currentValue );
                }
            }
            catch ( error )
            {
                cell.classList.remove( 'saving' );
                this.app.view.toast( 'An error occurred while updating description', 'error' );
                this._finishEditingDescription( cell, currentValue );
            }
        };

        input.addEventListener( 'blur', saveDescription );
        input.addEventListener( 'keydown', ( e ) => {
            if ( e.key === 'Enter' )
            {
                e.preventDefault();
                input.blur();
            }
            else if ( e.key === 'Escape' )
            {
                e.preventDefault();
                input.removeEventListener( 'blur', saveDescription );
                this._finishEditingDescription( cell, currentValue );
            }
        });
    }

    _finishEditingDescription( cell, value )
    {
        const input = cell.querySelector( '.description-input' );
        const textSpan = cell.querySelector( '.description-text' );
        const placeholderSpan = cell.querySelector( '.description-placeholder' );
        const editIcon = cell.querySelector( '.description-edit-icon' );

        if ( input )
        {
            input.remove();
        }

        textSpan.textContent = value;
        textSpan.style.display = value ? '' : 'none';
        placeholderSpan.textContent = value ? '' : 'Click to add description...';
        placeholderSpan.style.display = value ? 'none' : '';
        editIcon.style.display = '';
    }

    _togglePlaceholders( tmplIndex )
    {
        const content = this.app.view.pageContent.querySelector( `[data-type="placeholders-content"][data-template-index="${tmplIndex}"]` );
        const icon = this.app.view.pageContent.querySelector( `[data-type="placeholders-toggle-icon"][data-template-index="${tmplIndex}"]` );

        if ( content && icon )
        {
            const isVisible = content.style.display !== 'none';
            content.style.display = isVisible ? 'none' : 'block';
            icon.classList.toggle( 'ti-chevron-right', isVisible );
            icon.classList.toggle( 'ti-chevron-down', !isVisible );
        }
    }

    async _toggleStatus()
    {
        const isActive = this.pack.status === 'ACTIVE';
        const action = isActive ? 'deactivate' : 'activate';
        const actionLabel = isActive ? 'Deactivate' : 'Activate';

        const confirmed = await this.app.view.confirm(
            `${actionLabel} Pack`,
            `Do you want to ${action} this pack?`
        );

        if ( confirmed )
        {
            const pageContent = this.app.view.pageContent;
            const message = isActive ? 'Deactivating pack...' : 'Activating pack...';
            this.app.view.blockUI( pageContent, message );

            const newStatus = isActive ? 'INACTIVE' : 'ACTIVE';
            const { result } = await this.app.api.put(
                `/pack/${this.pack.id}/status`,
                { status: newStatus }
            );

            this.app.view.unblockUI( pageContent );

            if ( result.status === 200 && result.data )
            {
                this.pack = result.data;
                this.render();
                this._rebindEventListeners();
                this.app.view.toast( `Pack ${action}d successfully`, 'success' );
            }
            else
            {
                this.app.view.toast( `Failed to ${action} pack`, 'error' );
            }
        }
    }

    async deletePack()
    {
        const performDeletion = await this.app.view.confirm(
            "Delete Pack",
            "Do you really want to delete this pack?"
        );

        if ( true === performDeletion )
        {
            const { result } = await this.app.api.delete( `/pack/${this.packId}` );
            if ( result.status === 200 )
            {
                this.app.view.toast( "Pack deleted successfully", "success" );
                this.app.router.redirect( '/pack/list' );
            }
            else
            {
                this.app.view.toast( "Pack deletion failed", "error" );
            }
        }
    }

    switchTab( targetSelector )
    {
        const container = this.app.view.pageContent;

        container.querySelectorAll( '[data-type="pack-tab"]' ).forEach( ( tab ) => {
            tab.classList.remove( 'active' );
            tab.setAttribute( 'aria-selected', 'false' );
        });

        container.querySelectorAll( '.tab-pane' ).forEach( ( pane ) => {
            pane.classList.remove( 'show', 'active' );
        });

        const activeTab = container.querySelector( `[data-type="pack-tab"][data-tab-target="${targetSelector}"]` );
        if ( activeTab )
        {
            activeTab.classList.add( 'active' );
            activeTab.setAttribute( 'aria-selected', 'true' );
        }

        const activePane = container.querySelector( targetSelector );
        if ( activePane )
        {
            activePane.classList.add( 'show', 'active' );
        }

        if ( targetSelector === '#pills-generated-documents' )
        {
            this._initGeneratedDocumentsTable();
        }
    }

    async _initGeneratedDocumentsTable()
    {
        this._destroyGeneratedDocumentsTable();

        const tableContainer = this.app.view.pageContent.querySelector( '#generated-documents-table' );
        if ( !tableContainer )
        {
            return;
        }

        this.app.view.blockUI( tableContainer, 'Loading documents...' );

        let documents = [];
        try
        {
            const { result } = await this.app.api.get( `/generation/pack-list/${this.packId}` );
            documents = result.status === 200 && result.data?.documents ? result.data.documents : [];
        }
        catch ( error )
        {
            this.app.view.unblockUI( tableContainer );
            this.app.view.toast( 'Failed to load generated documents', 'error' );
            return;
        }

        this.app.view.unblockUI( tableContainer );

        if ( documents.length === 0 )
        {
            tableContainer.innerHTML = `
                <div class="text-center py-5">
                    <i class="ti ti-files display-4 text-muted mb-3"></i>
                    <h5 class="text-muted">No Generated Documents</h5>
                    <p class="text-muted mb-0">Documents generated from this pack will appear here.</p>
                </div>
            `;
            this.generatedDocumentsTable = true;
            return;
        }

        const formatFilesize = ( bytes ) => {
            if ( !bytes ) return '---';
            const kb = bytes / 1024;
            if ( kb < 1024 )
            {
                return `${kb.toFixed( 1 )} KB`;
            }
            return `${( kb / 1024 ).toFixed( 1 )} MB`;
        };

        this.generatedDocumentsData = documents.map( doc => [
            doc.filename,
            doc.createdAt ? new Date( doc.createdAt ).toLocaleString() : '---',
            formatFilesize( doc.filesize ),
            doc.filename
        ]);

        this.generatedDocumentsTable = new Grid({
            columns: [
                { name: 'Filename', sort: true },
                { name: 'Generation Date', sort: true },
                { name: 'Filesize', sort: true },
                {
                    id: 'actions',
                    name: '',
                    sort: false,
                    formatter: ( cell ) => {
                        if ( !cell ) return '---';
                        return html( `
                            <div class="d-flex gap-2">
                                <button type="button" data-type="download-document" data-filename="${cell}" class="btn btn-sm btn-outline-primary"><i class="ti ti-download me-1"></i>Download</button>
                                <button type="button" data-type="delete-document" data-filename="${cell}" class="btn btn-sm btn-outline-danger"><i class="ti ti-trash me-1"></i>Delete</button>
                            </div>
                        ` );
                    }
                }
            ],
            data: this.generatedDocumentsData,
            sort: true,
            pagination: {
                limit: 10
            },
            search: true,
            language: {
                search: {
                    placeholder: 'Search...'
                },
                pagination: {
                    previous: 'Previous',
                    next: 'Next',
                    showing: 'Showing',
                    of: 'of',
                    to: 'to',
                    results: 'results'
                },
                noRecordsFound: 'No generated documents found for this pack.'
            }
        });

        this.generatedDocumentsTable.render( tableContainer );
        this._bindDocumentDownloadButtons();
    }

    _bindDocumentDownloadButtons()
    {
        const tableContainer = this.app.view.pageContent.querySelector( '#generated-documents-table' );
        if ( !tableContainer ) return;

        // Remove previous listener if it exists to prevent duplicate firing
        if ( this._documentClickHandler )
        {
            tableContainer.removeEventListener( 'click', this._documentClickHandler );
        }

        this._documentClickHandler = async ( e ) => {
            const downloadButton = e.target.closest( '[data-type="download-document"]' );
            const deleteButton = e.target.closest( '[data-type="delete-document"]' );

            if ( downloadButton )
            {
                await this._handleDocumentDownload( downloadButton );
            }
            else if ( deleteButton )
            {
                await this._handleDocumentDelete( deleteButton );
            }
        };

        tableContainer.addEventListener( 'click', this._documentClickHandler );
    }

    async _handleDocumentDownload( button )
    {
        const filename = button.getAttribute( 'data-filename' );
        if ( !filename ) return;

        button.disabled = true;
        const originalContent = button.innerHTML;
        button.innerHTML = '<i class="ti ti-loader me-1"></i>Loading...';

        try
        {
            const response = await fetch( `${this.app.api.getUrl()}/generation/pack/${this.packId}/${filename}`, {
                method: 'GET',
                headers: {
                    'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                    'x-version': this.app.api._api.headers.get( 'x-version' )
                }
            });

            if ( !response.ok )
            {
                throw new Error( 'Download failed' );
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = filename;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
            window.URL.revokeObjectURL( url );
        }
        catch ( error )
        {
            this.app.view.toast( 'Failed to download document', 'error' );
        }
        finally
        {
            button.disabled = false;
            button.innerHTML = originalContent;
        }
    }

    async _handleDocumentDelete( button )
    {
        const filename = button.getAttribute( 'data-filename' );
        if ( !filename ) return;

        const confirmed = await this.app.view.confirm(
            'Delete Document',
            `Are you sure you want to delete "${filename}"?`
        );

        if ( !confirmed ) return;

        button.disabled = true;
        const originalContent = button.innerHTML;
        button.innerHTML = '<i class="ti ti-loader"></i>';

        try
        {
            const response = await fetch( `${this.app.api.getUrl()}/generation/pack/${this.packId}/${filename}`, {
                method: 'DELETE',
                headers: {
                    'x-sessiontoken': this.app.api._api.headers.get( 'x-sessiontoken' ),
                    'x-version': this.app.api._api.headers.get( 'x-version' )
                }
            });

            if ( !response.ok )
            {
                throw new Error( 'Delete failed' );
            }

            this.app.view.toast( 'Document deleted successfully', 'success' );

            this.generatedDocumentsData = this.generatedDocumentsData.filter( row => row[0] !== filename );
            this.generatedDocumentsTable.updateConfig({
                data: this.generatedDocumentsData
            }).forceRender();
        }
        catch ( error )
        {
            this.app.view.toast( 'Failed to delete document', 'error' );
            button.disabled = false;
            button.innerHTML = originalContent;
        }
    }

    render()
    {
        this._destroyGeneratedDocumentsTable();

        this.app.view.render(
            this.app.view.pageContent,
            template,
            {
                pack: this.pack,
                templates: this.templates
            }
        );
    }

    _destroyGeneratedDocumentsTable()
    {
        if ( this.generatedDocumentsTable )
        {
            if ( this.generatedDocumentsTable instanceof Grid )
            {
                this.generatedDocumentsTable.destroy();
            }

            const container = this.app.view.pageContent.querySelector( '#generated-documents-table' );
            if ( container )
            {
                container.innerHTML = '';
            }
            this.generatedDocumentsTable = null;
            this.generatedDocumentsData = null;
        }
    }

    getContentHeaderData()
    {
        return {
            title: this.pack ? this.pack.name : 'Pack Details',
            breadcrumb: [ 'Work', 'Packs', this.pack ? this.pack.name : 'Details' ]
        };
    }

    async exit()
    {
        this._destroyGeneratedDocumentsTable();
    }
}
